import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faUserPlus, faCheck, faArrowRight, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import './AddStudent.css';

/* =========================================================
   Admin › Users › Students › "Add student": a new student who came WITHOUT a free class.
   Paid (active: login email sent now) or Pending (reserved, not paid yet).
   No demo = no package bonus for a teacher (you can still give one in Earnings › Package bonuses).
   Then: open the profile to add the package (Payments), the teacher (Edit & teacher) and the classes.
   Backend: POST /api/admin/studentProfile/new-student (backend/api/Admin/NewStudent.js)
   ========================================================= */
const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const EMPTY = { firstName: '', lastName: '', age: '', mobile: '', email: '', offset: '', status: 'active', sendEmail: true };

const AddStudent = ({ onClose }) => {
  const [f, setF] = useState(EMPTY);
  const [zones, setZones] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/admin/studentprofile/OffsetList`, auth())
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setZones(list);
        const beirut = list.find((z) => /beirut/i.test(String(z.Timezone || '')));
        if (beirut) setF((x) => (x.offset ? x : { ...x, offset: String(beirut.Id) }));
      })
      .catch(() => setZones([]));
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !busy && onClose(!!done);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, done, onClose]);

  const set = (patch) => {
    setF((x) => ({ ...x, ...patch }));
    setError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.firstName.trim() || !f.lastName.trim()) return setError('Enter the first and last name.');
    const age = parseInt(f.age, 10);
    if (!Number.isInteger(age) || age < 4 || age > 99) return setError('Enter the age (4 to 99).');
    if (f.status === 'active' && f.sendEmail && !f.email.trim()) return setError('Add the parent’s email to send the login, or untick “Send the login email”.');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/studentProfile/new-student`, { ...f, age }, auth());
      setDone(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Couldn’t create the student.');
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  return (
    <div className="rmnewst" role="dialog" aria-modal="true" aria-label="Add a student">
      <button type="button" className="rmnewst-scrim" aria-label="Close" onClick={() => !busy && onClose(!!done)} />
      <div className="rmnewst-panel">
        <header className="rmnewst-head">
          <h2>
            <FontAwesomeIcon icon={faUserPlus} /> Add a student (no free class)
          </h2>
          <button type="button" className="rmnewst-x" onClick={() => !busy && onClose(!!done)} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>

        {done ? (
          <div className="rmnewst-done">
            <p className="rmnewst-ok">
              <FontAwesomeIcon icon={faCheck} /> {f.firstName} {f.lastName} was added as {done.status === 'pending' ? 'Pending (not paid yet)' : 'a paid student'}.
            </p>
            <dl>
              <div>
                <dt>Username</dt>
                <dd>{done.username}</dd>
              </div>
              <div>
                <dt>Password</dt>
                <dd>{done.password}</dd>
              </div>
            </dl>
            <p className="rmnewst-next">
              Next, on the profile: add the package in <b>Payments</b>, the teacher in <b>Edit & teacher</b>, then the classes.
              {done.status === 'pending' ? ' When they pay, set them Active in the subscription box.' : ''}
            </p>
            <a className="rmnewst-btn" href={`/admin/StudentProfile/${done.id}`}>
              Open the profile <FontAwesomeIcon icon={faArrowRight} />
            </a>
          </div>
        ) : (
          <form className="rmnewst-form" onSubmit={submit} noValidate>
            <div className="rmnewst-row">
              <label>
                First name
                <input value={f.firstName} onChange={(e) => set({ firstName: e.target.value })} autoFocus />
              </label>
              <label>
                Last name
                <input value={f.lastName} onChange={(e) => set({ lastName: e.target.value })} />
              </label>
            </div>
            <div className="rmnewst-row">
              <label>
                Age
                <input type="number" min="4" max="99" inputMode="numeric" value={f.age} onChange={(e) => set({ age: e.target.value })} />
              </label>
              <label>
                Time zone
                <select value={f.offset} onChange={(e) => set({ offset: e.target.value })}>
                  <option value="">Choose…</option>
                  {zones.map((z) => (
                    <option key={z.Id} value={z.Id}>
                      {z.Timezone || z.TimezoneName || `UTC${Number(z.TimeHours) >= 0 ? '+' : ''}${z.TimeHours}`}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="rmnewst-row">
              <label>
                Parent’s WhatsApp
                <input type="tel" value={f.mobile} onChange={(e) => set({ mobile: e.target.value })} placeholder="+961 71 123 456" />
              </label>
              <label>
                Parent’s email
                <input type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} placeholder="parent@email.com" />
              </label>
            </div>

            <div className="rmnewst-seg" role="radiogroup" aria-label="Status">
              {[
                ['active', 'Paid (active)'],
                ['pending', 'Pending (not paid yet)'],
              ].map(([k, label]) => (
                <button key={k} type="button" role="radio" aria-checked={f.status === k} className={f.status === k ? 'is-on' : ''} onClick={() => set({ status: k })}>
                  {label}
                </button>
              ))}
            </div>

            {f.status === 'active' ? (
              <label className="rmnewst-check">
                <input type="checkbox" checked={f.sendEmail} onChange={(e) => set({ sendEmail: e.target.checked })} />
                <span>Send the login email to the parent now</span>
              </label>
            ) : (
              <p className="rmnewst-note">Pending: no login email yet. It goes when you set them Active after they pay.</p>
            )}
            <p className="rmnewst-note">No free class, so no package bonus for a teacher. You can still give one in Earnings › Package bonuses.</p>

            {error && (
              <p className="rmnewst-error" role="alert">
                <FontAwesomeIcon icon={faCircleExclamation} /> {error}
              </p>
            )}

            <button type="submit" className="rmnewst-btn" disabled={busy}>
              {busy ? 'Adding…' : 'Add student'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddStudent;
